import type { VehicleStrategy, VehiclePhysicsConfig } from './VehicleStrategy'

export class CarStrategy implements VehicleStrategy {
  private readonly config: VehiclePhysicsConfig = {
    productiveGForce: 0.95, // Cars can achieve higher combined g
    hardBrakingThreshold: -8.0, // Four tires = better braking
    hardAccelerationThreshold: 4.0, // AWD advantage
    canLean: false,
    tractionLimitLeanAngle: undefined,
    minimumCorneringG: 0.8, // Need to corner harder than motorcycles
    engineBrakeThreshold: 2.0, // Less engine braking than bikes
    moderateBrakingThreshold: -4.0,
    moderateAccelerationThreshold: 2.0,
  }

  public getVehicleType = (): string => {
    return 'car'
  }

  public getPhysicsConfig = (): VehiclePhysicsConfig => {
    return this.config
  }

  public calculateCoastingIntensity = (
    acceleration: number,
    leanAngle: number, // Should always be ~0 for cars
    lateralG: number,
    combinedG: number,
    _speed: number
  ): number => {
    // Cars don't lean - ignore lean angle (should be ~0)
    // If lean angle is significant, data might be corrupted
    if (Math.abs(leanAngle) > 5) {
      // Suspicious data - treat as unknown
      return 0.5
    }

    // Check combined g-force first - cars need higher threshold
    if (combinedG >= this.config.productiveGForce) {
      return 0
    }

    // Very hard braking - cars can brake harder than bikes
    if (acceleration < this.config.hardBrakingThreshold) {
      return 0
    }

    const isAccelerating = acceleration > 0
    const isBraking = acceleration < 0
    const absAcceleration = Math.abs(acceleration)

    // BRAKING LOGIC: Cars can brake very hard
    if (isBraking) {
      return this.calculateBrakingCoastingIntensity(absAcceleration, lateralG)
    }

    // ACCELERATION LOGIC: Cars have good power-to-weight
    if (isAccelerating) {
      const accelIntensity =
        this.calculateAccelerationCoastingIntensity(absAcceleration)
      if (accelIntensity !== null) {
        return accelIntensity
      }
      // Fall through to combined g-force logic
    }

    // Neutral or low acceleration - use combined g-force
    return this.calculateCombinedGForceCoastingIntensity(combinedG, lateralG)
  }

  private readonly calculateBrakingCoastingIntensity = (
    absAcceleration: number,
    lateralG: number
  ): number => {
    if (absAcceleration < this.config.engineBrakeThreshold) {
      // Very light braking or just engine braking
      // Cars have less engine braking than motorcycles
      if (lateralG < 0.5) {
        // Straight line or gentle corner - should be braking harder
        // 0 m/s² = 90% coasting, 2 m/s² (engine brake) = 55% coasting
        const intensity =
          0.9 - (absAcceleration / this.config.engineBrakeThreshold) * 0.35
        return Math.max(0.55, intensity)
      }
      // High cornering - engine braking might be appropriate
      return 0.3
    } else if (absAcceleration < 4.0) {
      // Light to moderate braking (2-4 m/s²)
      if (lateralG < 0.6) {
        // Not cornering hard - should brake more
        // 2 m/s² = 55%, 4 m/s² = 25%
        const intensity = 0.55 - ((absAcceleration - 2.0) / 2.0) * 0.3
        return Math.max(0.25, intensity)
      }
      // Cornering moderately hard
      return 0.2
    } else if (absAcceleration < 8.0) {
      // Moderate to hard braking (4-8 m/s²)
      if (lateralG < 0.4) {
        // Gentle cornering - could brake harder still
        // 4 m/s² = 25%, 8 m/s² = 5%
        const intensity = 0.25 - ((absAcceleration - 4.0) / 4.0) * 0.2
        return Math.max(0.05, intensity)
      }
      // Cornering while braking moderately
      return 0
    }

    // Very hard braking (>8 m/s²) - always productive
    return 0
  }

  private readonly calculateAccelerationCoastingIntensity = (
    absAcceleration: number
  ): number | null => {
    if (absAcceleration >= this.config.hardAccelerationThreshold) {
      // Good acceleration (>4 m/s²) - full throttle
      return 0
    } else if (absAcceleration >= this.config.moderateAccelerationThreshold) {
      // Moderate acceleration (2-4 m/s²) - could be full throttle at high speed
      // Scale: 4 m/s² = 0%, 2 m/s² = 20%
      const intensity =
        ((4.0 - absAcceleration) / this.config.moderateAccelerationThreshold) *
        0.2
      return Math.max(0, intensity)
    }

    // Low acceleration (<2 m/s²) - use combined g-force logic
    return null
  }

  private readonly calculateCombinedGForceCoastingIntensity = (
    combinedG: number,
    lateralG: number
  ): number => {
    // Check if cornering hard enough (cars should corner harder than bikes)
    if (lateralG >= this.config.minimumCorneringG) {
      return 0 // Cornering productively
    }

    let intensity: number

    if (combinedG >= this.config.productiveGForce) {
      // >0.95g combined - productive driving
      intensity = 0
    } else if (combinedG >= 0.7) {
      // 0.7-0.95g combined - moderate effort
      // Linear scale: 0.95g = 0%, 0.7g = 35%
      intensity = ((this.config.productiveGForce - combinedG) / 0.25) * 0.35
    } else if (combinedG >= 0.4) {
      // 0.4-0.7g combined - light effort, starting to coast
      // Linear scale: 0.7g = 35%, 0.4g = 65%
      intensity = 0.35 + ((0.7 - combinedG) / 0.3) * 0.3
    } else {
      // <0.4g combined - definitely coasting
      // Scale: 0.4g = 65%, 0g = 100%
      intensity = 0.65 + ((0.4 - combinedG) / 0.4) * 0.35
    }

    // Clamp to valid range [0, 1]
    return Math.max(0, Math.min(1, intensity))
  }

  public validateTelemetry = (
    _acceleration: number,
    leanAngle: number,
    _speed: number
  ): boolean => {
    // Cars should never lean more than a few degrees
    // If lean angle is significant, data is likely from a motorcycle
    return Math.abs(leanAngle) < 10
  }
}
